/* Atelier — product page, advanced pass.

   Native scroll choreography, so the page gains motion without shipping a 70KB animation
   library onto a store already loading Vitals, Klaviyo, GTM, Globo and Track123.

   Two hard-won notes, both from bugs caught in testing:

   1. Reveal animation is applied ONLY to elements this theme owns. An earlier version also
      animated the Vitals widgets; because Vitals injects asynchronously, a widget could
      receive opacity:0 and never be revealed, leaving a blank gap where the reviews should
      be. Invisible reviews are far worse than unanimated ones.

   2. The review collapse must measure the masonry's natural height BEFORE the stylesheet's
      max-height clamp is taken into account. Reading scrollHeight on an already-clamped,
      overflow:hidden element returns the clamped value, so the code concluded there was
      nothing to expand and never rendered the button — truncating the reviews with no way
      to open them. */
(function () {
  'use strict';

  var reduced = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
  var root = document.documentElement;

  /* ------------------------------------------------- reveal + stagger ---- */

  function observe(els) {
    if (!els.length) return;
    if (reduced || !('IntersectionObserver' in window)) {
      els.forEach(function (e) { e.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    els.forEach(function (e) { io.observe(e); });
  }

  function markTargets() {
    // Only this theme's own elements. Never app-injected widgets — see note 1.
    var el = document.querySelector('.gf-pdp .product__description');
    if (el) el.classList.add('gf-rv2');

    var trust = document.querySelector('.gf-pdp-trust');
    if (trust) trust.classList.add('gf-stagger');

    observe([].slice.call(document.querySelectorAll('.gf-rv2:not(.is-in), .gf-stagger:not(.is-in)')));
  }

  /* Failsafe: anything carrying a reveal class that is still transparent after 4 seconds
     gets shown. A missed observer must never cost the shopper the content. */
  function failsafe() {
    setTimeout(function () {
      document.querySelectorAll('.gf-rv2, .gf-stagger').forEach(function (e) {
        if (getComputedStyle(e).opacity === '0') e.classList.add('is-in');
      });
    }, 4000);
  }

  /* ------------------------------------------------------ magnetic CTA --- */

  function magneticCta() {
    if (reduced || matchMedia('(hover: none)').matches) return;
    var btn = document.querySelector('.gf-pdp .product-form__submit');
    if (!btn) return;
    btn.addEventListener('mousemove', function (e) {
      var r = btn.getBoundingClientRect();
      var x = (e.clientX - r.left - r.width / 2) * 0.12;
      var y = (e.clientY - r.top - r.height / 2) * 0.22;
      btn.style.transform = 'translate(' + x.toFixed(1) + 'px,' + y.toFixed(1) + 'px)';
    });
    btn.addEventListener('mouseleave', function () { btn.style.transform = ''; });
  }

  /* --------------------------------------------------- reviews collapse -- */

  function naturalHeight(el) {
    // Lift the clamp, read the true height, put it back. Without this the measurement is
    // just the clamp value — see note 2 at the top of this file.
    var prevMax = el.style.maxHeight;
    var prevOverflow = el.style.overflow;
    el.style.maxHeight = 'none';
    el.style.overflow = 'visible';
    var h = el.scrollHeight;
    el.style.maxHeight = prevMax;
    el.style.overflow = prevOverflow;
    return h;
  }

  function reviewsCollapse() {
    var widget = document.querySelector('.gf-pdp .vtl-pr-main-widget--product-page');
    if (!widget || widget.dataset.gfCollapsed) return;
    var cards = widget.querySelector('.vtl-pr-main-widget__cards, .vtl-pr-masonry');
    if (!cards) return;

    var full = naturalHeight(cards);
    if (full <= 700) {
      // Nothing worth hiding. Release the clamp so the reviews are never truncated.
      cards.style.maxHeight = 'none';
      return;
    }
    widget.dataset.gfCollapsed = '1';

    var fade = document.createElement('div');
    fade.className = 'gf-pdp-reviews-fade';
    cards.appendChild(fade);

    var count = widget.querySelectorAll('.vtl-pr-review-card').length;
    var label = count ? ('Read all ' + count + ' reviews') : 'Read all reviews';
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'gf-pdp-reviews-more';
    btn.setAttribute('aria-expanded', 'false');
    btn.textContent = label;
    cards.parentNode.insertBefore(btn, cards.nextSibling);

    btn.addEventListener('click', function () {
      var open = widget.classList.toggle('is-expanded');
      btn.setAttribute('aria-expanded', String(open));
      btn.textContent = open ? 'Show fewer reviews' : label;
      if (!open) widget.scrollIntoView({ behavior: reduced ? 'instant' : 'smooth', block: 'start' });
    });
  }

  /* ------------------------------------------------- delivery estimate --- */

  /* A range, never a single date. This store ships worldwide from a supplier, so a fixed
     promise would be one it cannot keep. */
  function deliveryEstimate() {
    var host = document.querySelector('[data-pdp-deliver]');
    if (!host) return;
    var lo = parseInt(host.getAttribute('data-lo') || '7', 10);
    var hi = parseInt(host.getAttribute('data-hi') || '14', 10);
    var fmt = function (d) { return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' }); };
    var a = new Date(); a.setDate(a.getDate() + lo);
    var b = new Date(); b.setDate(b.getDate() + hi);
    var out = host.querySelector('[data-pdp-deliver-text]');
    if (out) out.innerHTML = 'Order today, estimated arrival <b>' + fmt(a) + ' to ' + fmt(b) + '</b>';
  }

  /* ----------------------------------------------------- saving badge ---- */

  function savingBadge() {
    var price = document.querySelector('.gf-pdp .product__info-container .price');
    if (!price || !price.classList.contains('price--on-sale')) return;
    if (price.querySelector('.gf-pdp-save')) return;
    var sale = price.querySelector('.price-item--sale');
    var was = price.querySelector('s .price-item, .price-item--regular');
    if (!sale || !was) return;
    var num = function (el) { return parseFloat((el.textContent || '').replace(/[^0-9.]/g, '')); };
    var s = num(sale), w = num(was);
    if (!(w > s) || !isFinite(s) || !isFinite(w)) return;
    var pct = Math.round(((w - s) / w) * 100);
    if (pct < 5) return;
    var b = document.createElement('span');
    b.className = 'gf-pdp-save';
    b.textContent = 'Save ' + pct + '%';
    sale.parentNode.appendChild(b);
  }

  function init() {
    if (!root.classList.contains('gf-pdp')) return;
    markTargets();
    failsafe();
    magneticCta();
    deliveryEstimate();
    savingBadge();

    // Vitals injects asynchronously, so poll briefly for the reviews widget.
    var tries = 0;
    var poll = setInterval(function () {
      tries++;
      if (document.querySelector('.vtl-pr-main-widget--product-page .vtl-pr-review-card')) {
        reviewsCollapse();
        clearInterval(poll);
      }
      if (tries > 24) clearInterval(poll);
    }, 500);
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();